// Shared, cached copy of the user's tagged collection, used by the Collection and Shuffle pages.
// Shows the last known copy instantly (memory, then localStorage) and refreshes in the background.
import { ref } from 'vue';
import { getTaggedCollection } from '@/services/discogs';

const STORAGE_KEY = 'collection_cache_v2';
// After this long, a visit triggers a background refresh
const FRESH_MS = 5 * 60 * 1000;

// Module-level state: one copy for the whole app
const releases = ref([]);
const loadedAt = ref(0);
const isLoading = ref(false); // nothing to show yet
const isRefreshing = ref(false); // showing cached data while fetching
const error = ref(null);
// Set once we edit a record on Discogs: the server's cached copy is then out of date, so the next
// fetch must bypass it. Persisted so a reload before that fetch doesn't lose it.
let needsFreshFetch = false;
// Counts edits, so a response to a request sent before an edit can be recognised as out of date
let editCount = 0;
let inFlight = null;

// Storage can be unavailable (private mode, quota); the app then just keeps data in memory
const readStored = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return stored && Array.isArray(stored.releases) ? stored : null;
  } catch (err) {
    return null;
  }
};

const writeStored = () => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      releases: releases.value, loadedAt: loadedAt.value, needsFreshFetch,
    }));
  } catch (err) {
    // Memory-only is fine
  }
};

const fetchFresh = () => {
  if (inFlight) return inFlight;

  isRefreshing.value = releases.value.length > 0;
  const refresh = needsFreshFetch;
  const editsAtStart = editCount;
  inFlight = getTaggedCollection({ refresh })
    .then(data => {
      // Edited since this request went out: the response predates the edit, so keep our copy and
      // leave the flag set for the next fetch
      if (editCount !== editsAtStart) return;
      if (refresh) needsFreshFetch = false;
      releases.value = data.releases || [];
      loadedAt.value = Date.now();
      error.value = null;
      writeStored();
    })
    .catch(() => {
      // Keep showing cached data if we have it; only surface the error when there's nothing to show
      if (!releases.value.length) error.value = 'Could not load your collection. Try signing in again.';
    })
    .finally(() => {
      inFlight = null;
      isLoading.value = false;
      isRefreshing.value = false;
    });
  return inFlight;
};

/**
 * Makes the collection available, from cache when possible.
 * @param {Object} options - { force: true } refreshes even if the cache is fresh
 */
export const loadCollection = async ({ force = false } = {}) => {
  if (!releases.value.length) {
    const stored = readStored();
    if (stored) {
      releases.value = stored.releases;
      loadedAt.value = stored.loadedAt || 0;
      needsFreshFetch = needsFreshFetch || Boolean(stored.needsFreshFetch);
    }
  }

  const isFresh = Date.now() - loadedAt.value < FRESH_MS;
  if (releases.value.length && isFresh && !force) return;

  if (!releases.value.length) isLoading.value = true;
  await fetchFresh();
};

// Merge changes into one cached record after it was edited on Discogs, so no refetch is needed
export const updateRelease = (instanceId, changes) => {
  needsFreshFetch = true;
  editCount += 1;
  releases.value = releases.value.map(r => (r.instanceId === instanceId ? { ...r, ...changes } : r));
  writeStored();
};

// Forget everything (on logout)
export const clearCollection = () => {
  releases.value = [];
  loadedAt.value = 0;
  error.value = null;
  needsFreshFetch = false;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (err) {
    // Nothing stored
  }
};

export default function useCollection() {
  return {
    releases, isLoading, isRefreshing, error, loadCollection, updateRelease,
  };
}
